import React, { useCallback, useState, useMemo, memo, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { AppRow } from '../../layout/appRow';
import { useSearchParams } from 'react-router-dom';
import { AppButtonOutlined } from '../app/button/appButtonOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import { AppControlledCalendar } from '../app/appControlledCalendar';
import { AppButtonContained } from '../app/button/appButtonContained';
import { AppToolbox } from '../app/appToolbox';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useUserRoles } from '../../hooks/useUserRoles';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';
import {
  ReportsField,
  FiltersFormData,
  MonthSelect,
  SearchOptions,
  ViewMode,
} from '../../models/reports';
import { Employee, RolesNames } from '../../models/employee';
import { endOfMonth, format, isValid, startOfMonth } from 'date-fns';
import {
  getFirstDayPrevMonth,
  getLastDayPrevMonth,
} from '../../services/dateService';
import {
  useGetEmployeesQuery,
  useGetManagersQuery,
  useGetProjectsQuery,
  useGetEmployeesByProjectIdQuery,
} from '../../store/reports/reportsService';
import { useFiltersDefaultValues } from '../../hooks/reports/useFiltersDefaultValues';
import {
  useReportsFilters,
  useReportsFiltersDispatch,
  useReportsViewMode,
} from '../../context/reports/useReportsCtx';
import { useAppDispatch } from '../../hooks';
import { ExportDialog } from './exportDialog';
import { Report } from '../../models/report';
import { downloadExcelReport } from '../../services/api/reportsServices';
import { downloadExcel } from '../../services/downloadService';
import useAppSnackbar from '../../hooks/useAppSnackbar';
import { AppConfirmationSuccessful } from '../app/confirmation/appConfirmationSuccessful';
import {
  selectIsDialogVisible,
  setIsExportDialogVisible,
} from '../../store/reports/reportsSlice';
import { useSelector } from 'react-redux';
import {
  NO_TOWERS,
  NO_EMPLOYEES,
  NO_MANAGERS,
  NO_PROJECTS,
  PLEASE_TYPE,
  DEFAULT_PAGE_SIZE,
} from '../../constants';
import { getUserRoleIdByViewMode } from '../../services/reportsService';
import { AxiosError } from 'axios';
import { EmployeeAllocationsModal } from './employeeAllocationsModal';
import { ProjectAllocationsModal } from './projectAllocationsModal';
import { generateUserWithIdLabel, getOptions } from '../../services/employeeService';
import { generateProjectLabel } from '../../services/projectService';
import { Tower } from '../../models/tower';
import { useGetTowersQuery } from '../../store/towers/towersService';

const makeStyles = () => ({
  autocompleteContainer: {
    mt: 1,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 2,
  },
  autocomplete: {
    display: 'flex',
    flex: 1,
    minWidth: '170px !important',
  },
  datesRow: {
    mt: 2,
    mb: 3,
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 2,
  },
  quickMonthSelectBox: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: 1,
  },
  quickMonthSelectText: {
    mr: 2,
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  monthChipsBox: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    flex: 2,
    gap: 1,
  },
  monthChip: {
    display: 'flex',
    flex: 1,
    maxHeight: '40px',
    borderRadius: '50px',
  },
  dateSelectBox: {
    display: 'flex',
    flex: 1,
    gap: 2,
    justifyContent: 'space-between',
  },
  dateSelect: {
    minWidth: '120px',
    display: 'flex',
    flex: 1,
  },
});

interface Props {
  handleDisplay: (data: FiltersFormData) => void;
  viewUnloggedProjects?: boolean;
}

enum SearchField {
  ENGINEER = 'ENGINEER',
  MANAGER = 'MANAGER',
  PROJECT = 'PROJECT',
  TOWER = 'TOWER',
}

const defaultQueryParams = {
  includeDeleted: true,
  includeInactive: false,
  showHierarchy: false,
  take: 10,
};

const today = new Date();

function ReportsToolboxBase({
  handleDisplay,
  viewUnloggedProjects,
}: Props): JSX.Element {
  const styles = makeStyles();
  const isMobile = useMediaQuery((themeMedia: Theme) =>
    themeMedia.breakpoints.down('sm')
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const { onErrorSnackbar } = useAppSnackbar();
  const filters = useReportsFilters();
  const dispatchFilters = useReportsFiltersDispatch();
  const dispatch = useAppDispatch();
  const isExportDialogVisible = useSelector(selectIsDialogVisible);
  const [viewMode, setViewMode] = useReportsViewMode();

  const userRoles = useUserRoles();
  const hasMultipleRoles = useMemo(
    () => Object.values(userRoles).filter((role) => role).length > 1,
    [userRoles]
  );

  const methods = useForm<FiltersFormData>({
    defaultValues: {
      startDate: startOfMonth(today),
      endDate: endOfMonth(today),
      monthSelect: MonthSelect.CURRENT,
      manager: null,
      tower: null,
    },
  });

  const { setValue, control, reset: resetForm, trigger, resetField } = methods;

  const { isLoading: isLoadingDefaultValues } = useFiltersDefaultValues({
    resetForm,
  });

  const [isOpenViewMode, setIsOpenViewMode] = useState<boolean>(true);
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] =
    useState<boolean>(false);
  const [isOpenAllocationsModal, setIsOpenAllocationsModal] =
    useState<boolean>(false);
  const [initialManager, setInitialManager] = useState<Employee | null>(null);

  const [
    watchProject,
    watchManager,
    watchEngineer,
    monthSelect,
    startDate,
    endDate,
    watchTowers,
  ] = useWatch({
    name: [
      'project',
      ReportsField.MANAGER,
      'engineer',
      'monthSelect',
      ReportsField.START_DATE,
      ReportsField.END_DATE,
      ReportsField.TOWER,
    ] ,
    control,
  });

  const sharedPayload = {
    startDate: isValid(startDate) ? format(startDate, 'yyyy-MM-dd') : '',
    endDate: isValid(endDate) ? format(endDate, 'yyyy-MM-dd') : '',
    managerId: watchManager?.id ?? null,
    employeeId: watchEngineer?.id ?? null,
    projectId: watchProject?.id ?? null,
    towerId: watchTowers?.id ?? null,
    towerName: watchTowers?.name ?? null,
    managerName: watchManager?.name ?? null,
    employeeName: watchEngineer?.name ?? null,
    projectName: watchProject?.name ?? null,
    projectCode: watchProject?.code ?? null,
    monthSelect,
    showHierarchy: filters?.showHierarchy ?? false,
    includeInactive: filters?.includeInactive ?? false,
    includeDeleted: filters?.includeInactive ?? false,
  };

  const { isAdmin, isTeamLead, isProjectManager, isBudgetOwner } = userRoles;

  const isAllFieldsEmpty = useMemo(
    () => !watchProject && !watchManager && !watchEngineer,

    [watchProject, watchManager, watchEngineer]
  );

  const isActionsDisabled = useMemo(
    () => !isAdmin && isAllFieldsEmpty,
    [isAdmin, isAllFieldsEmpty]
  );

  const roleId = useMemo(() => getUserRoleIdByViewMode(viewMode), [viewMode]);

  const [searchOptions, setSearchOptions] = useState<
    SearchOptions<SearchField>
  >({
    value: '',
  });

  useEffect(() => {
    if (watchManager && !initialManager) {
      setInitialManager(watchManager);
    }
  }, [watchManager]);

  // GET TOWERS
  const { data: towersQueryData, isLoading: isLoadingTowers } =
    useGetTowersQuery(
      {
        take: DEFAULT_PAGE_SIZE,
        ...(searchOptions.value && {
          filter: searchOptions.field === SearchField.TOWER
            ? searchOptions.value
            : '',
        }),
      },
      { skip: isLoadingDefaultValues || !!watchTowers?.id }
    );


  // GET EMPLOYEES
  const { data: employees, isFetching: isFetchingEmployees } =
    useGetEmployeesQuery(
      {
        ...defaultQueryParams,
        managerId: watchManager?.id,
        userRoleId: roleId,
        filter:
          searchOptions.field === SearchField.ENGINEER
            ? searchOptions.value
            : '',
        includeDeleted: sharedPayload.includeDeleted,
        includeInactive: sharedPayload.includeInactive,
        showHierarchy: sharedPayload.showHierarchy,
      },
      { skip: isLoadingDefaultValues || !!watchProject?.id }
    );

  // GET EMPLOYEES BY PROJECT
  const { data: employeesByProject, isFetching: isFetchingEmployeesByProject } =
    useGetEmployeesByProjectIdQuery(
      {
        projectId: watchProject?.id,
        params: {
          ...defaultQueryParams,
          managerId: watchManager?.id,
          userRoleId: roleId,
          filter:
            searchOptions.field === SearchField.ENGINEER
              ? searchOptions.value
              : '',
          includeDeleted: sharedPayload.includeDeleted,
          includeInactive: sharedPayload.includeInactive,
          showHierarchy: sharedPayload.showHierarchy,
        },
      },

      { skip: !watchProject?.id }
    );

  const generateEmployeesList = useCallback(
    (employeesList: Employee[] | undefined) => {
      return (
        employeesList?.map((employee) => ({
          ...employee,
          name: generateUserWithIdLabel(employee.name, employee.internalId),
        })) || []
      );
    },
    []
  );

  const generateTowerList = useCallback(
    (towerList: Tower[] | undefined) => {
      return (
        towerList?.map((tower) => ({
          ...tower,
          name: generateUserWithIdLabel(tower.name, tower.id),
        })) || []
      );
    },
    []
  );

  const employeesOptions = useMemo(() => {
    if (watchProject) {
      return generateEmployeesList(employeesByProject);
    }

    return generateEmployeesList(employees);
  }, [employees, employeesByProject, generateEmployeesList, watchProject]);

  const towerOptions = useMemo(() => {
    return towersQueryData ? [...towersQueryData.items] : [];
  }, [towersQueryData]);

  // GET MANAGERS
  const { data: managers, isFetching: isFetchingManagers } =
    useGetManagersQuery({
      ...defaultQueryParams,
      includeDeleted: false,
      includeInactive: true,
      roleId,
      filter:
        searchOptions.field === SearchField.MANAGER ? searchOptions.value : '',
    });

  // GET PROJECTS
  const { data: projects, isFetching: isFetchingProjects } =
    useGetProjectsQuery(
      {
        take: 10,
        userRoleId: roleId,
        managerId: watchManager?.id,
        filter:
          searchOptions.field === SearchField.PROJECT
            ? searchOptions.value
            : '',
      },
      {
        skip: !!watchEngineer || isLoadingDefaultValues,
      }
    );

  const projectsOptions = useMemo(() => {
    return projects || [];
  }, [projects]);

  const onResetSearch = useCallback(() => {
    setSearchOptions({ value: '' });
  }, []);

  const onSelectManager = useCallback(
    (data) => {
      if (data) {
        setValue(ReportsField.PROJECT, null);
        setValue(ReportsField.ENGINEER, null);
      }
      onResetSearch();
    },
    [setValue, onResetSearch]
  );

  const onSelectProject = useCallback(
    (data) => {
      if (data) {
        setValue(ReportsField.ENGINEER, null);
      }
      onResetSearch();
    },
    [setValue, onResetSearch]
  );

  const searchEngineers = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.ENGINEER });
  }, []);

  const searchTowers = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.TOWER });
  }, []);

  const searchProjects = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.PROJECT });
  }, []);

  const searchManagers = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.MANAGER });
  }, []);

  const resetDateFields = useCallback(() => {
    resetField(ReportsField.START_DATE);
    resetField(ReportsField.END_DATE);
  }, [resetField]);

  const onCurrentMonthSelect = useCallback(() => {
    resetDateFields();
    setValue(ReportsField.MONTH_SELECT, MonthSelect.CURRENT);
    setValue(ReportsField.START_DATE, startOfMonth(today));
    setValue(ReportsField.END_DATE, endOfMonth(today));
  }, [resetDateFields, setValue]);

  const onPreviousMonthSelect = useCallback(() => {
    resetDateFields();
    setValue(ReportsField.MONTH_SELECT, MonthSelect.PREVIOUS);
    setValue(ReportsField.START_DATE, getFirstDayPrevMonth(today));
    setValue(ReportsField.END_DATE, getLastDayPrevMonth(today));
  }, [resetDateFields, setValue]);

  const onCustomMonthSelect = useCallback(() => {
    resetDateFields();
    setValue(ReportsField.MONTH_SELECT, MonthSelect.CUSTOM);
    setValue(ReportsField.START_DATE, startOfMonth(today));
    setValue(ReportsField.END_DATE, endOfMonth(today));
  }, [resetDateFields, setValue]);

  const handleOpenViewMode = useCallback(() => {
    setIsOpenViewMode((prev) => !prev);
  }, []);

  const onClearFields = useCallback(() => {
    dispatchFilters({
      startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(today), 'yyyy-MM-dd'),
      monthSelect: MonthSelect.CURRENT,
      managerId: isAdmin ? null : initialManager?.id ?? null,
      managerName: isAdmin ? null : initialManager?.name ?? null,
      employeeId: null,
      projectId: null,
      employeeName: null,
      projectName: null,
      projectCode: null,
      showHierarchy: false,
      includeInactive: false,
      employeeLocation: '',
      towerId: null,
      skip: null,
    });
    resetForm();
  }, [initialManager, dispatchFilters, isAdmin, resetForm]);

  const handleViewModeChange = useCallback(
    (e) => {
      const newViewMode = e.target.getAttribute('data-view-mode');
      setViewMode(newViewMode);
      setSearchParams({ viewMode: newViewMode });
      onClearFields();
    },
    [onClearFields, setSearchParams, setViewMode]
  );

  const titlePage = useMemo(() => {
    switch (viewMode) {
      case ViewMode.PEOPLE:
        return 'Activity schedule';
      case ViewMode.PROJECT:
        return 'Reports';
      default:
        return '';
    }
  }, [viewMode]);

  const onCloseConfirmDialog = useCallback(() => {
    setIsOpenSuccessDialog(false);
  }, []);

  const handleDownloadReportExcel = useCallback(
    async (report: Report) => {
      if (!report.isValid()) {
        onErrorSnackbar(new Error(report.filterErrorMessage));

        return;
      }

      try {
        const data = await downloadExcelReport(report);
        downloadExcel(data);
        setIsOpenSuccessDialog(true);
        setTimeout(() => setIsOpenSuccessDialog(false), 2000);
      } catch (error) {
        onErrorSnackbar(error as Error | AxiosError);
      }
    },
    [onErrorSnackbar]
  );

  const handleOpenExportDialog = useCallback(async () => {
    const isFormValid = await trigger();

    if (!isFormValid) return;

    dispatch(setIsExportDialogVisible(!isExportDialogVisible));
  }, [dispatch, isExportDialogVisible, trigger]);

  const handleShowAllocations = useCallback(async () => {
    const isFormValid = await trigger();

    if (!isFormValid) return;

    setIsOpenAllocationsModal((prev) => !prev);
  }, [trigger]);

  const isViewModesVisible = useMemo(
    () => isAdmin || hasMultipleRoles,
    [hasMultipleRoles, isAdmin]
  );

  const isCurrentMonth = useMemo(
    () => monthSelect === MonthSelect.CURRENT,
    [monthSelect]
  );

  const isPreviousMonth = useMemo(
    () => monthSelect === MonthSelect.PREVIOUS,
    [monthSelect]
  );

  const isCustomMonth = useMemo(
    () => monthSelect === MonthSelect.CUSTOM,
    [monthSelect]
  );

  const managerInputLabel = useMemo(() => {
    switch (viewMode) {
      case ViewMode.PEOPLE:
        return RolesNames.TEAM_LEAD;

      case ViewMode.PROJECT:
        return RolesNames.PROJECT_MANAGER;

      default:
        return 'Manager';
    }
  }, [viewMode]);

  const isManagerReportsFieldDisabled = useMemo(() => {
    if (viewMode === ViewMode.PROJECT) {
      return !(isAdmin || isBudgetOwner || isProjectManager);
    }

    if (viewMode === ViewMode.PEOPLE) {
      return !isAdmin;
    }

    return false;
  }, [isAdmin, isBudgetOwner, isProjectManager, viewMode]);

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleDisplay)}>
          <AppToolbox
            preventHide
            pageTitle={!isMobile ? titlePage : ''}
            firstChildren={
              isViewModesVisible && (
                <Box display="flex" alignItems="center" flexWrap="wrap">
                  {!isMobile && (
                    <IconButton sx={{ mx: 1.5 }} onClick={handleOpenViewMode}>
                      <ViewModuleOutlinedIcon />
                    </IconButton>
                  )}

                  {isOpenViewMode && (
                    <Box
                      display="flex"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <Typography variant="body2">View mode:</Typography>
                      <AppRow
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'flex-start',
                          gap: 2,
                        }}
                      >
                        {(isTeamLead || isAdmin) && (
                          <Button
                            onClick={handleViewModeChange}
                            disabled={viewMode == ViewMode.PEOPLE}
                            variant="contained"
                            data-view-mode={ViewMode.PEOPLE}
                          >
                            People
                          </Button>
                        )}
                        {(isProjectManager || isAdmin || isBudgetOwner) && (
                          <Button
                            onClick={handleViewModeChange}
                            disabled={viewMode == ViewMode.PROJECT}
                            variant="contained"
                            data-view-mode={ViewMode.PROJECT}
                          >
                            Projects
                          </Button>
                        )}
                      </AppRow>
                    </Box>
                  )}
                </Box>
              )
            }
            secondChildren={
              !isMobile && (
                <>
                  <AppButtonOutlined
                    handleClick={handleShowAllocations}
                    hideIcon
                    sx={{ mr: 2 }}
                    label="% Allocations"
                  />
                  <AppButtonOutlined
                    handleClick={handleOpenExportDialog}
                    sx={{ mr: 2 }}
                    label="Export"
                    isDisabled={isActionsDisabled}
                  />
                  <AppButtonContained
                    label="Display"
                    isDisabled={isActionsDisabled}
                    loading={isLoadingDefaultValues}
                    type="submit"
                  />
                </>
              )
            }
            thirdChildren={
              <>
                <AppRow sx={styles.autocompleteContainer}>
                  <AppControlledAutocomplete
                    name={ReportsField.MANAGER}
                    getOptionLabel={(manager) =>
                      typeof manager === 'string' ? manager : manager.name
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    label={managerInputLabel}
                    options={managers || []}
                    onSelect={onSelectManager}
                    searchOptions={searchManagers}
                    sx={styles.autocomplete}
                    isLoading={isFetchingManagers}
                    noOptionsText={NO_MANAGERS}
                    placeholder={PLEASE_TYPE}
                    disabled={isManagerReportsFieldDisabled}
                  />

                  <AppControlledAutocomplete
                    name={ReportsField.PROJECT}
                    getOptionLabel={(project) =>
                      typeof project === 'string'
                        ? project
                        : generateProjectLabel(project.name, project.code)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    label="Project"
                    options={projectsOptions}
                    onSelect={onSelectProject}
                    searchOptions={searchProjects}
                    sx={styles.autocomplete}
                    isLoading={isFetchingProjects}
                    noOptionsText={NO_PROJECTS}
                    placeholder={PLEASE_TYPE}
                  />

                  <AppControlledAutocomplete
                    name={ReportsField.ENGINEER}
                    getOptionLabel={(engineer) =>
                      typeof engineer === 'string' ? engineer : engineer.name
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    label="Engineer"
                    options={employeesOptions}
                    onSelect={onResetSearch}
                    searchOptions={searchEngineers}
                    sx={styles.autocomplete}
                    isLoading={
                      isFetchingEmployees || isFetchingEmployeesByProject
                    }
                    noOptionsText={NO_EMPLOYEES}
                    placeholder={PLEASE_TYPE}
                  />

                  <AppControlledAutocomplete
                    name={ReportsField.TOWER}
                    getOptionLabel={(tower) =>
                      typeof tower === 'string' ? tower : tower.name
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    label="Tower"
                    options={towerOptions}
                    onSelect={onResetSearch}
                    searchOptions={searchTowers}
                    sx={styles.autocomplete}
                    isLoading={
                      isFetchingEmployees || isFetchingEmployeesByProject
                    }
                    noOptionsText={NO_TOWERS}
                    placeholder={PLEASE_TYPE}
                  />
                </AppRow>

                <AppRow sx={styles.datesRow}>
                  <Box sx={styles.quickMonthSelectBox}>
                    <Typography
                      variant="body2"
                      sx={styles.quickMonthSelectText}
                    >
                      Quick month select:
                    </Typography>
                    <Box sx={styles.monthChipsBox}>
                      <Button
                        onClick={onCurrentMonthSelect}
                        disabled={isCurrentMonth}
                        variant="contained"
                        sx={styles.monthChip}
                      >
                        {MonthSelect.CURRENT}
                      </Button>
                      <Button
                        onClick={onPreviousMonthSelect}
                        disabled={isPreviousMonth}
                        variant="contained"
                        sx={styles.monthChip}
                      >
                        {MonthSelect.PREVIOUS}
                      </Button>
                      <Button
                        onClick={onCustomMonthSelect}
                        disabled={isCustomMonth}
                        variant="contained"
                        sx={styles.monthChip}
                      >
                        {MonthSelect.CUSTOM}
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={styles.dateSelectBox}>
                    <AppControlledCalendar
                      label="Start Date"
                      name={ReportsField.START_DATE}
                      isDisabled={!isCustomMonth}
                      sx={styles.dateSelect}
                      maxDate={endDate || undefined}
                      rules={{
                        validate: {
                          startBeforeEnd: (value: Date) =>
                            !isValid(endDate) ||
                            value <= endDate ||
                            'Start date must be less than or equal to end date',
                        },
                      }}
                    />

                    <AppControlledCalendar
                      label="End Date"
                      name={ReportsField.END_DATE}
                      isDisabled={!isCustomMonth}
                      sx={styles.dateSelect}
                      minDate={startDate || undefined}
                    />
                  </Box>
                </AppRow>

                {isMobile && (
                  <Box>
                    <Box sx={{ flex: 1, marginBottom: 2 }}>
                      <AppButtonContained
                        label="Display"
                        isDisabled={isActionsDisabled}
                        loading={isLoadingDefaultValues}
                        type="submit"
                        sx={{ width: '100%' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <AppButtonOutlined
                          handleClick={handleOpenExportDialog}
                          label="Export"
                          sx={{ width: '100%' }}
                          isDisabled={isActionsDisabled}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <AppButtonOutlined
                          handleClick={handleShowAllocations}
                          hideIcon
                          sx={{ width: '100%' }}
                          label="% Allocations"
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
              </>
            }
          />
        </form>
      </FormProvider>
      <ExportDialog
        filters={sharedPayload}
        viewMode={viewMode}
        isOpen={isExportDialogVisible}
        handleClose={handleOpenExportDialog}
        handleDownloadReportExcel={handleDownloadReportExcel}
      />
      <AppConfirmationSuccessful
        isOpen={isOpenSuccessDialog}
        message="Successfully exported"
        onClose={onCloseConfirmDialog}
      />

      {sharedPayload && (
        <>
          {viewMode === ViewMode.PEOPLE ? (
            <EmployeeAllocationsModal
              filters={sharedPayload}
              handleClose={handleShowAllocations}
              isOpen={isOpenAllocationsModal}
            />
          ) : (
            <ProjectAllocationsModal
              filters={sharedPayload}
              viewUnloggedProjects={!!viewUnloggedProjects}
              handleClose={handleShowAllocations}
              isOpen={isOpenAllocationsModal}
            />
          )}
        </>
      )}
    </>
  );
}

export const ReportsToolbox = memo(ReportsToolboxBase);
